import type { FormInitialValues } from '../typings';
import type {
  SaveQueue,
  SaveQueueFlusher,
  SaveQueueSnapshot,
  SaveStatus,
} from './save-queue.types';

type CreateSaveQueueOptions = {
  /** Total attempts for one batch before the queue reports an error. */
  maxAttempts?: number;
};

/**
 * Per-form save queue: coalesces edits by field id, allows one in-flight request
 * at a time, and retries failures without dropping newer edits.
 */
export const createSaveQueue = (
  flusher: SaveQueueFlusher,
  options: CreateSaveQueueOptions = {},
): SaveQueue => {
  const maxAttempts = options.maxAttempts ?? 3;

  const pending = new Map<string, FormInitialValues[string]>();
  const listeners = new Set<() => void>();

  let inflight: Promise<void> | null = null;
  // `flush` drains until the queue is empty; `flushWaiting` asks for one batch per call.
  let drainAll = false;
  let batchRequested = false;
  // The batch handed to the flusher but not yet acknowledged. Kept by value, not just by
  // id: on the unload path the request carrying it is about to be killed and the values
  // are the only copy left. The snapshot's field ids derive from this, so the two facts
  // cannot drift apart.
  let inFlightBatch: FormInitialValues = {};
  let status: SaveStatus = 'idle';
  let lastSavedAt: number | null = null;
  let snapshot: SaveQueueSnapshot = {
    status,
    lastSavedAt,
    pendingFieldIds: [],
  };

  const publish = () => {
    snapshot = {
      status,
      lastSavedAt,
      // Deduped: a field re-edited while its own save is in flight appears in
      // both lists, and that is the most common thing a typing user does.
      pendingFieldIds: [...new Set([...Object.keys(inFlightBatch), ...pending.keys()])],
    };

    listeners.forEach(listener => {
      // A broken subscriber must not reject drain() and destroy the taken batch.
      try {
        listener();
      } catch (error) {
        console.error('Save queue subscriber threw', error);
      }
    });
  };

  const setStatus = (next: SaveStatus) => {
    status = next;
    publish();
  };

  const takePending = (): FormInitialValues => {
    const batch: FormInitialValues = {};
    pending.forEach((value, fieldId) => {
      batch[fieldId] = value;
    });
    pending.clear();
    return batch;
  };

  // A retry must not resurrect a stale value over an edit made while the request was in flight.
  const requeueFailed = (batch: FormInitialValues) => {
    Object.entries(batch).forEach(([fieldId, value]) => {
      if (!pending.has(fieldId)) {
        pending.set(fieldId, value);
      }
    });
  };

  const drain = async (): Promise<void> => {
    while (pending.size > 0 && (drainAll || batchRequested)) {
      batchRequested = false;
      let batch = takePending();
      inFlightBatch = batch;
      setStatus('saving');

      let accepted = false;

      for (let attempt = 1; attempt <= maxAttempts && !accepted; attempt++) {
        try {
          accepted = await flusher(batch);
        } catch (error) {
          // Rejection is the common production failure (network, timeout, 5xx).
          // Swallowing it silently would leave no trace of why a save never landed.
          console.error('Save queue flush rejected', error);
          accepted = false;
        }

        if (!accepted && attempt < maxAttempts) {
          // Merge the failed batch back so newer edits win, then take just those
          // fields again. Never clear all of `pending` — it may hold edits to
          // OTHER fields made while this request was in flight. Build a fresh
          // object rather than mutating one the flusher may have retained.
          requeueFailed(batch);

          const retryBatch: FormInitialValues = {};
          Object.keys(batch).forEach(fieldId => {
            retryBatch[fieldId] = pending.get(fieldId) as FormInitialValues[string];
            pending.delete(fieldId);
          });
          batch = retryBatch;
          inFlightBatch = batch;
        }
      }

      if (!accepted) {
        inFlightBatch = {};
        requeueFailed(batch);
        setStatus('error');
        return;
      }

      inFlightBatch = {};
      lastSavedAt = Date.now();
    }

    setStatus('saved');
  };

  const startDrain = (): Promise<void> => {
    if (inflight) return inflight;
    if (pending.size === 0) {
      drainAll = false;
      batchRequested = false;
      return Promise.resolve();
    }

    let settle!: () => void;

    // `inflight` must be set before drain() runs its synchronous prefix: drain()
    // calls setStatus('saving') synchronously, which notifies subscribers, and a
    // subscriber that re-enters flush() has to join this promise rather than
    // start a second concurrent drain.
    inflight = new Promise<void>(resolve => {
      settle = resolve;
    });

    void drain()
      // drain() should not reject — publish() isolates subscribers and the
      // flusher's rejection is caught. Catching anyway keeps a future edit to
      // drain() from surfacing as an unhandled rejection with the queue wedged.
      .catch(error => {
        console.error('Save queue drain failed', error);
      })
      .finally(() => {
        inflight = null;
        drainAll = false;
        batchRequested = false;
        settle();
      });

    return inflight;
  };

  // Joining an in-flight one-batch drain upgrades it to drain until empty.
  const flush = (): Promise<void> => {
    drainAll = true;
    return startDrain();
  };

  const flushWaiting = (): Promise<void> => {
    batchRequested = true;
    return startDrain();
  };

  return {
    enqueue: (fieldId, value) => {
      pending.set(fieldId, value);
      publish();
    },
    flush,
    flushWaiting,
    takeUnacknowledged: () => {
      const batch = { ...inFlightBatch, ...takePending() };
      inFlightBatch = {};
      // drain() keeps its own local `batch` reference and does not know these values
      // were just taken. If the page survives (bfcache, mobile background) and that
      // in-flight flusher call later resolves false, requeueFailed() re-adds the same
      // values and drain() reports 'error' — a duplicate send and a spurious error
      // chip, not data loss, since the beacon already carried the same values out.
      //
      // 'saving' and 'error' both describe pending/inFlightBatch, now empty — a spinner
      // would hang over nothing, and a Retry button would call flush() into an instant
      // no-op (pending.size === 0). 'idle' is what an empty queue actually supports; the
      // beacon hasn't resolved yet, so 'saved' would be a guess. A send that later fails
      // re-enqueues (flushSaveQueuesOnUnload's onFailed) and earns 'error' back through
      // drain() the normal way. 'saved' is left alone — still true regardless of this take.
      if (status === 'saving' || status === 'error') {
        status = 'idle';
      }
      publish();
      return batch;
    },
    peekValues: () => {
      const values: FormInitialValues = { ...inFlightBatch };
      pending.forEach((value, fieldId) => {
        values[fieldId] = value;
      });
      return values;
    },
    discard: fieldIds => {
      const taken = { values: {} as FormInitialValues, status };
      fieldIds.forEach(fieldId => {
        if (!pending.has(fieldId)) return;
        taken.values[fieldId] = pending.get(fieldId) as FormInitialValues[string];
        pending.delete(fieldId);
      });
      if (Object.keys(taken.values).length === 0) return taken;

      // A Retry would flush nothing, as after takeUnacknowledged.
      if (status === 'error' && pending.size === 0) {
        status = 'idle';
      }
      publish();
      return taken;
    },
    restore: (values, previousStatus) => {
      if (!Object.keys(values).some(fieldId => !pending.has(fieldId))) return;

      requeueFailed(values);
      // Brings Retry back; a drain under way sends them and reports its own status.
      if (previousStatus === 'error' && !inflight) {
        status = 'error';
      }
      publish();
    },
    getSnapshot: () => snapshot,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};
